/** biome-ignore-all lint/correctness/useHookAtTopLevel: Hook counts are the same, we're just choosing different signatures */
import {
  type MutationFunction,
  type QueryFunction,
  type QueryKey,
  type UseMutationOptions,
  type UseQueryOptions,
  type UseQueryResult,
  useMutation as useOriginalMutation,
  useQuery as useOriginalQuery,
} from '@tanstack/react-query'

/**
 * Wrapper for useMutation that supports both v4 and v5 API patterns.
 *
 * v4 pattern: useMutation(mutationFn, options)
 * v5 pattern: useMutation(options) where mutationFn is in options
 *
 * This wrapper allows the v4 pattern to work with v5 by converting it internally.
 */
export function useMutation<
  TData = unknown,
  TError = unknown,
  TVariables = void,
  TContext = unknown,
>(
  mutationFnOrOptions:
    | MutationFunction<TData, TVariables>
    | UseMutationOptions<TData, TError, TVariables, TContext>,
  options?: UseMutationOptions<TData, TError, TVariables, TContext>,
) {
  // If the first argument is a function, it's the v4 pattern
  if (typeof mutationFnOrOptions === 'function') {
    return useOriginalMutation({
      mutationFn: mutationFnOrOptions,
      ...options,
    })
  }

  // Otherwise, it's already the v5 pattern
  return useOriginalMutation(mutationFnOrOptions)
}

/**
 * Wrapper for useQuery that supports both v4 and v5 API patterns.
 *
 * v4 pattern: useQuery(queryKey, queryFn, options)
 * v5 pattern: useQuery(options) where queryKey and queryFn are in options
 *
 * This wrapper allows the v4 pattern to work with v5 by converting it internally.
 */
export function useQuery<
  TQueryFnData = unknown,
  TError = unknown,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
>(
  queryKeyOrOptions:
    | TQueryKey
    | UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>,
  queryFn?: QueryFunction<TQueryFnData, TQueryKey>,
  options?: Omit<
    UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>,
    'queryKey' | 'queryFn'
  >,
): UseQueryResult<TData, TError> {
  // If the first argument is an array, string, or if queryFn is provided, it's the v4 pattern
  if (
    Array.isArray(queryKeyOrOptions) ||
    typeof queryKeyOrOptions === 'string' ||
    (queryKeyOrOptions &&
      typeof queryKeyOrOptions === 'object' &&
      !('queryKey' in queryKeyOrOptions) &&
      queryFn)
  ) {
    return useOriginalQuery({
      queryKey: queryKeyOrOptions as TQueryKey,
      queryFn: queryFn!,
      ...options,
    })
  }

  // Otherwise, it's already the v5 pattern
  return useOriginalQuery(
    queryKeyOrOptions as UseQueryOptions<
      TQueryFnData,
      TError,
      TData,
      TQueryKey
    >,
  )
}

// Re-export everything else from @tanstack/react-query
export * from '@tanstack/react-query'
